/**
 * Template card component (P1-T12, restyled for the 2026-10-17 Home design).
 *
 * Reusable across Home rails/grids, template detail and Collection: one card
 * with a lazy-loaded thumbnail, a media-type chip (image / video), an optional
 * "New" badge, a play button for video, and the effect title over a gradient.
 * Tap → `#/template/:slug`.
 *
 * No engagement numbers are shown: `viewSeed` is seed data, not real activity
 * (master-context.md → template cards).
 *
 * Performance notes (60fps on mid-range phones):
 * - Fixed `aspect-ratio` thumbnail box → no layout shift while images load.
 * - `loading="lazy"` + `decoding="async"` on every <img>.
 * - Skeleton shimmer uses a translated gradient overlay (transform-only).
 * - Card press feedback is transform/opacity-only.
 */
import type { Template } from '@loomia/shared';
import { escapeHtml } from './placeholder.js';
import { icon } from './icon.js';

/**
 * Card-size copy of a bundled cover (480×640, made by scripts/make-thumbs.mjs).
 * Other URLs (CDN, uploads) are returned unchanged.
 */
export function cardThumbUrl(url: string): string {
  const m = /^\/img\/templates\/([^/]+\.webp)$/.exec(url);
  return m && !m[1]!.endsWith('-before.webp') ? `/img/templates/thumb/${m[1]}` : url;
}

/** One template card. The <img> starts with a shimmer until it loads. */
export function templateCardHtml(t: Template, opts: { explore?: boolean } = {}): string {
  const isVideo = t.mediaKind === 'video';
  const isNew = t.badges.includes('new') || t.isNew;
  const chips = [
    `<span class="tpl-chip is-kind">${icon(isVideo ? 'play_arrow' : 'image', { fill: true })}<span class="tpl-chip-tx">${opts.explore ? (isVideo ? 'AI Video' : 'AI Photo') : isVideo ? 'Video' : 'Image'}</span></span>`,
    ...(isNew ? ['<span class="tpl-chip is-new">New</span>'] : []),
    ...(t.badges.includes('hot')
      ? [
          opts.explore
            ? `<span class="tpl-chip is-pop">${icon('workspace_premium', { fill: true })}Popular</span>`
            : '<span class="tpl-chip is-hot">Hot</span>',
        ]
      : []),
  ].join('');

  return `
    <a class="tpl-card" href="#/template/${encodeURIComponent(t.slug)}" aria-label="${escapeHtml(t.title)}${isVideo ? ' (video)' : ''}">
      <span class="tpl-thumb">
        <img
          src="${escapeHtml(cardThumbUrl(t.thumbnailUrl))}"
          alt=""
          loading="lazy"
          decoding="async"
          data-tpl-img
        />
        <span class="tpl-chips">${chips}</span>
        ${isVideo ? `<span class="tpl-play" aria-hidden="true">${icon('play_arrow', { fill: true })}</span>` : ''}
        <span class="tpl-title">${escapeHtml(t.title)}</span>
      </span>
    </a>`;
}

/** Skeleton cards shown while a rail/grid loads (same box model → no shift). */
export function templateCardSkeletonHtml(count = 6): string {
  return Array.from(
    { length: count },
    () => `
    <div class="tpl-card is-skeleton" aria-hidden="true">
      <span class="tpl-thumb"><span class="shimmer"></span></span>
    </div>`,
  ).join('');
}

/**
 * Attach load/error handlers to card images inside `container`.
 * On load the shimmer fades out; on error the thumbnail keeps its
 * placeholder background instead of a broken-image icon.
 */
export function bindCardImages(container: HTMLElement): void {
  container.querySelectorAll('img[data-tpl-img]').forEach((node) => {
    const img = node as HTMLImageElement;
    const done = (): void => img.classList.add('is-loaded');
    if (img.complete && img.naturalWidth > 0) {
      done();
    } else {
      img.addEventListener('load', done, { once: true });
      const onError = (): void => {
        // Missing card thumbnail: fall back to the full cover once.
        const full = img.src.replace('/img/templates/thumb/', '/img/templates/');
        if (full !== img.src) {
          img.src = full;
          return;
        }
        img.removeEventListener('error', onError);
        img.classList.add('is-broken');
      };
      img.addEventListener('error', onError);
    }
  });
}
